import type {
  ConditionalArgument,
  RoutineVariable,
  StoredConditionVariable,
} from '@sb/remote-control/types';
import { DISTANCE_UNIT_INFOS } from '@sb/remote-control/util/distance';
import { FORCE_UNIT_INFOS } from '@sb/remote-control/util/force';
import type { Expression } from '@sb/routine-runner';

type StoredVariable = StoredConditionVariable;

function isExpression(value: unknown): value is Expression {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value.kind === 'JavaScript' ||
      value.kind === 'Python' ||
      value.kind === 'constant')
  );
}

const IO_PORT_LABEL = /^kinematicState\.ioState\['([^']+)'\]$/;

/**
 * Converts a value from base units (meters/newtons) to display units
 * using the unit label to determine the target unit.
 */
function convertToDisplayUnit(value: number, variable: StoredVariable): number {
  const { unitLabel } = variable;

  if (!unitLabel) return value;

  const unitType =
    variable.kind === 'equipment' ? variable.unitType : undefined;

  if (variable.kind === 'distance' || unitType === 'distance') {
    const unitInfo = DISTANCE_UNIT_INFOS.find(
      (info) => info.abbreviation === unitLabel,
    );

    return unitInfo ? unitInfo.fromMeters(value) : value;
  }

  if (variable.kind === 'force' || unitType === 'force') {
    const unitInfo = FORCE_UNIT_INFOS.find(
      (info) => info.abbreviation === unitLabel,
    );

    return unitInfo ? unitInfo.fromNewtons(value) : value;
  }

  return value;
}

/**
 * The editor keeps rows the stored schema rejects so the user can repair
 * them, so despite the type, any identity field may be missing at runtime.
 */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Names a stored condition variable from its own fields. Rows written through
 * the routine DSL carry no display fields, so each kind falls back to the
 * fields that identify it.
 */
export function describeStoredConditionVariable(
  variable: StoredVariable,
): string {
  switch (variable.kind) {
    case 'step': {
      const name =
        asString(variable.displayName) ??
        asString(variable.variableName) ??
        'Step variable';

      const stepName = asString(variable.stepName);

      return stepName
        ? `${variable.stepPosition ?? '?'}. ${stepName}: ${name}`
        : name;
    }
    case 'ioState':
      return (
        asString(variable.variableName)?.match(IO_PORT_LABEL)?.[1] ??
        asString(variable.displayName) ??
        (typeof variable.port === 'number' ? `Port ${variable.port}` : 'I/O')
      );
    case 'gripperState':
      return `Gripper: ${
        asString(variable.displayName) ?? asString(variable.selector) ?? 'state'
      }`;
    case 'force': {
      const name =
        asString(variable.displayName) ??
        asString(variable.variableName) ??
        'Force';

      const sensor = asString(variable.sensor);

      return sensor ? `${sensor}: ${name}` : name;
    }
    case 'sensorState':
      return (
        asString(variable.displayName) ??
        asString(variable.sensorID) ??
        'Sensor'
      );
    case 'equipment':
      return (
        asString(variable.displayName) ??
        asString(variable.selector) ??
        'Equipment'
      );
    case 'cartesianPosition':
      return (
        asString(variable.displayName) ??
        asString(variable.selector) ??
        'Robot pose'
      );
    case 'distance':
      return asString(variable.displayName) ?? 'Linear Distance';
    case 'jointPose':
      return asString(variable.displayName) ?? 'Joint pose';
    case 'expression':
      return asString(variable.displayName) ?? 'Expression';
    default:
      return (
        asString(variable.displayName) ??
        asString(variable.variableName) ??
        'Variable'
      );
  }
}

/** The value kind a stored variable compares as, when it doesn't say. */
function getVariableKind(
  variable: StoredVariable,
): RoutineVariable.Kind | undefined {
  if (variable.variableKind) return variable.variableKind;

  switch (variable.kind) {
    case 'distance':
    case 'cartesianPosition':
    case 'equipment':
    case 'force':
      return 'number';
    case 'ioState':
    case 'sensorState':
      return 'enum';
    default:
      return undefined;
  }
}

/**
 * Maps a raw value to its display string: an enum or boolean value to its
 * display name, a number to display units with its unit label.
 */
function formatValue(
  value: RoutineVariable.VariableValue,
  variable: StoredVariable,
): string {
  const variableKind = getVariableKind(variable);

  if (variableKind === 'boolean' || variableKind === 'enum') {
    const option = variable.values?.find((v) => v.value === value);

    return option?.displayName || String(value);
  }

  if (variableKind === 'number') {
    const displayValue =
      typeof value === 'number' ? convertToDisplayUnit(value, variable) : value;

    const str = String(displayValue);

    return variable.unitLabel ? `${str} ${variable.unitLabel}` : str;
  }

  return String(value);
}

/**
 * Formats expression values (JavaScript/Python expressions)
 */
function formatExpressionValue(value: Expression): string {
  if (value.kind === 'JavaScript' || value.kind === 'Python') {
    return value.expression;
  }

  if (value.kind === 'constant') {
    return String(value.value);
  }

  return '';
}

/**
 * Formats a single condition into a human-readable string
 *
 * @example
 * formatCondition({
 *   variable: { kind: 'gripperState', displayName: 'isOpen', ... },
 *   operator: { operator: '==', displayName: '==' },
 *   value: true
 * })
 * // => "Gripper: isOpen == true"
 */
export function formatCondition(
  condition: ConditionalArgument.ConditionalBuilderState,
): string {
  const { variable, operator, value } = condition;

  if (!variable || !operator || value === null || value === undefined) {
    return '';
  }

  const variableName = describeStoredConditionVariable(variable);

  const operatorSymbol = operator.displayName || operator.operator;

  // Expressions can be values for ANY variable type
  const valueStr = isExpression(value)
    ? formatExpressionValue(value)
    : formatValue(value, variable);

  return `${variableName} ${operatorSymbol} ${valueStr}`;
}

/**
 * Formats an AND group of conditions into a readable string
 *
 * @example
 * // Single condition
 * formatConditionGroup([condition1]) // => "Gripper: isOpen == true"
 *
 * @example
 * // Multiple conditions
 * formatConditionGroup([condition1, condition2])
 * // => "Gripper: isOpen == true AND Input 1 > 5"
 */
export function formatConditionGroup(
  conditions: ConditionalArgument.ConditionalBuilderState[],
): string {
  const formattedConditions = conditions
    .map(formatCondition)
    .filter((s) => s.length > 0);

  return formattedConditions.join(' AND ');
}
